import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { CancelButton, SubmitButton } from "../../components/btns";
import { errorAlert, successAlert } from "../../components/toastr";
import { createEvent, updateEvent } from "../../resources/api/events";
import { APIURL } from "../../resources/fetch";
import { FileUpload } from "../../components/files";

export const emptyEvent = {
    title: "",
    description: "",
    location: "",
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    status: "draft",
    client_logo_file_id: null
};

const EventForm = ({ show, setShow, event, onSave }) => {
    const action = event?.id ? "edit" : "create";
    const [details, setDetails] = useState(emptyEvent);
    const [validated, setValidated] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show) setDetails({ ...emptyEvent, ...(event || {}) });
    }, [event, show]);

    const change = e => {
        const { name, value, type, checked } = e.currentTarget;
        setDetails(d => ({ ...d, [name]: type === "checkbox" ? checked : value }));
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (!e.currentTarget.checkValidity()) {
            setValidated(true);
            return errorAlert("Some required event details are missing.", "Form Errors");
        }

        const data = {
            title: details.title,
            description: details.description,
            location: details.location,
            start_date: details.start_date,
            start_time: details.start_time || null,
            end_date: details.end_date || null,
            end_time: details.end_time || null,
            status: details.status,
            client_logo_file_id: details.client_logo_file_id || null
        };

        setSubmitting(true);
        const request = action === "edit" ? updateEvent(data, details.id) : createEvent(data);
        request
            .then(({ event: savedEvent, message }) => {
                successAlert(message);
                onSave(savedEvent);
                setValidated(false);
                setShow(false);
            })
            .catch(errorAlert)
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal show={show} onHide={() => setShow(false)} centered animation={false} size="lg" backdrop="static">
            <Modal.Header closeButton className="bg-secondary text-light">
                <Modal.Title>{action === "edit" ? "Edit Event" : "Create Event"}</Modal.Title>
            </Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row className="g-2">
                        <Col sm={8}>
                            <Form.Label className="form-field-title">Title</Form.Label>
                            <Form.Control name="title" value={details.title} onChange={change} required />
                            <Form.Control.Feedback type="invalid">Event title is required.</Form.Control.Feedback>
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="form-field-title">Status</Form.Label>
                            <Form.Select name="status" value={details.status} onChange={change} required>
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="form-field-title">Description</Form.Label>
                            <Form.Control as="textarea" rows={3} name="description" value={details.description || ""} onChange={change} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="form-field-title">Location</Form.Label>
                            <Form.Control name="location" value={details.location} onChange={change} required />
                            <Form.Control.Feedback type="invalid">Event location is required.</Form.Control.Feedback>
                        </Col>
                        <Col sm={3}>
                            <Form.Label className="form-field-title">Start Date</Form.Label>
                            <Form.Control type="date" name="start_date" value={details.start_date} onChange={change} required />
                        </Col>
                        <Col sm={3}>
                            <Form.Label className="form-field-title">Start Time</Form.Label>
                            <Form.Control type="time" name="start_time" value={details.start_time || ""} onChange={change} />
                        </Col>
                        <Col sm={3}>
                            <Form.Label className="form-field-title">End Date</Form.Label>
                            <Form.Control type="date" name="end_date" min={details.start_date} value={details.end_date || ""} onChange={change} />
                        </Col>
                        <Col sm={3}>
                            <Form.Label className="form-field-title">End Time</Form.Label>
                            <Form.Control type="time" name="end_time" value={details.end_time || ""} onChange={change} />
                        </Col>
                        <Col xs={12} className="mt-3">
                            <Form.Label className="form-field-title d-block">Client Logo</Form.Label>
                            {details.client_logo_file_id ? (
                                <div className="d-flex align-items-center gap-3 border rounded p-2">
                                    <img src={`${APIURL}/files/${details.client_logo_file_id}/view/image`} alt="Client logo preview" className="event-logo-preview" />
                                    <div className="flex-grow-1 small text-muted">Client logo uploaded</div>
                                    <Button type="button" size="sm" variant="outline-danger" onClick={() => setDetails(d => ({ ...d, client_logo_file_id: null }))}>
                                        <i className="fas fa-trash-alt me-1" />Remove
                                    </Button>
                                </div>
                            ) : (
                                <FileUpload accept="image" size="sm" variant="outline-secondary" onUpload={file => setDetails(d => ({ ...d, client_logo_file_id: file.id }))}>
                                    <i className="fas fa-upload me-1" />Upload Client Logo
                                </FileUpload>
                            )}
                            <Form.Text muted className="d-block">Use a clear PNG, JPG, or other supported image file.</Form.Text>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="py-1">
                    <SubmitButton type="submit" isSubmitting={isSubmitting}>{action === "edit" ? "Update Event" : "Create Event"}</SubmitButton>
                    <CancelButton type="button" isSubmitting={isSubmitting} onClick={() => setShow(false)}>Cancel</CancelButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default EventForm;
